/** Thresholds for valorization bilans recommendations — keep in sync with the guide. */
export const VALORIZATION_RECO_THRESHOLDS = {
  SORTING_ERROR_HIGH: 15,
  SHARE_HIGH_IMPACT: 20,
  SORTING_GAIN_HIGH: 3,
  OBJECTIVE_GAP_HIGH: 5,
};

export const roundPercent = (value) => Math.round(value * 10) / 10;

export const getSortingErrorImpact = (row) =>
  roundPercent(((row?.share || 0) * (row?.sortingErrorRate || 0)) / 100);

/**
 * Build diagnostic messages for the "Problème principal" block.
 * Volume frein and quality frein are separate — cause is always tied to the named flux.
 */
export const buildValorizationMainIssues = (rows, t) => {
  if (!rows?.length) {
    return {
      messages: [t("No valorization data for this period")],
      allNonValorisable: false,
    };
  }

  const messages = [];
  const allNonValorisable = rows.every((row) => !row.isValorizationEligible);

  if (allNonValorisable) {
    messages.push(t("Main issue no valorisable config"));
  }

  const volumeFrein = [...rows]
    .filter((row) => !row.isValorizationEligible)
    .sort((a, b) => b.share - a.share)[0];

  const qualityFrein = [...rows]
    .filter(
      (row) =>
        row.sortingErrorRate >= VALORIZATION_RECO_THRESHOLDS.SORTING_ERROR_HIGH,
    )
    .sort((a, b) => getSortingErrorImpact(b) - getSortingErrorImpact(a))[0];

  const shareBasis = t("valorization share basis without sorting errors");

  if (
    volumeFrein &&
    qualityFrein &&
    volumeFrein.label === qualityFrein.label &&
    !allNonValorisable
  ) {
    messages.push(
      t("Main issue volume and quality same flux", {
        flux: volumeFrein.label,
        share: `${volumeFrein.share}%`,
        shareBasis,
        rate: `${qualityFrein.sortingErrorRate}%`,
      }),
    );
  } else {
    if (volumeFrein && volumeFrein.share > 0 && !allNonValorisable) {
      messages.push(
        t("Main issue volume frein", {
          flux: volumeFrein.label,
          share: `${volumeFrein.share}%`,
          shareBasis,
        }),
      );
    }

    if (qualityFrein) {
      messages.push(
        t("Main issue quality frein", {
          flux: qualityFrein.label,
          rate: `${qualityFrein.sortingErrorRate}%`,
          impact: `${getSortingErrorImpact(qualityFrein)}%`,
        }),
      );
    }
  }

  if (!messages.length) {
    messages.push(t("Main issue none"));
  }

  return { messages, allNonValorisable, volumeFrein, qualityFrein };
};

/**
 * Build actionable recommendations for the Valorization bilans panel.
 */
export const buildValorizationRecommendationActions = ({
  rows,
  currentRate,
  objectivePercent,
  t,
  allNonValorisable = false,
}) => {
  if (!rows?.length) return [];

  const actions = [];
  const { SHARE_HIGH_IMPACT, SORTING_GAIN_HIGH, OBJECTIVE_GAP_HIGH } =
    VALORIZATION_RECO_THRESHOLDS;

  if (allNonValorisable) {
    actions.push({
      id: "configure-valorisable",
      text: t("Recommendation configure valorisable fluxes"),
      impactLevel: "high",
      impactValue: 100,
    });
  }

  const nonValorisableRows = [...rows]
    .filter((row) => !row.isValorizationEligible)
    .sort((a, b) => b.share - a.share);

  if (!allNonValorisable && nonValorisableRows.length > 0) {
    const top = nonValorisableRows[0];
    actions.push({
      id: "source-separation",
      text: t("Recommendation prioritize non valorisable flux", {
        flux: top.label,
        share: `${top.share}%`,
        impact: `${top.share}%`,
      }),
      impactLevel: top.share >= SHARE_HIGH_IMPACT ? "high" : "medium",
      impactValue: top.share,
    });
  }

  const topSortingErrorRow = [...rows]
    .filter((row) => row.sortingErrorRate > 0)
    .sort((a, b) => getSortingErrorImpact(b) - getSortingErrorImpact(a))[0];

  if (topSortingErrorRow) {
    const estimatedGain = getSortingErrorImpact(topSortingErrorRow);
    actions.push({
      id: "reduce-sorting-errors",
      text: t("Recommendation reduce sorting errors flux", {
        flux: topSortingErrorRow.label,
        rate: `${topSortingErrorRow.sortingErrorRate}%`,
        impact: `${estimatedGain}%`,
      }),
      impactLevel: estimatedGain >= SORTING_GAIN_HIGH ? "high" : "medium",
      impactValue: estimatedGain,
    });
  }

  if (currentRate < objectivePercent) {
    const gapValue = roundPercent(objectivePercent - currentRate);
    actions.push({
      id: "close-objective-gap",
      text: t("Recommendation close objective gap", {
        gap: `${gapValue}%`,
      }),
      impactLevel: gapValue >= OBJECTIVE_GAP_HIGH ? "high" : "medium",
      impactValue: gapValue,
    });
  }

  if (!actions.length) {
    actions.push({
      id: "maintain",
      text: t("Recommendation maintain performance"),
      impactLevel: "low",
      impactValue: 0,
    });
  }

  return actions.sort((a, b) => b.impactValue - a.impactValue).slice(0, 3);
};
